import { type CSSProperties, memo, useCallback, useMemo } from 'react';
import isEqual from 'react-fast-compare';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type PublicBoard } from '@air/api/types';
import { ChevronRightIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { useBreakpointsContext } from '@air/provider-media-query';

import { GalleryItemType, type ItemProps } from '~/components/Gallery/types';
import { useGetPublicBoardMenuOptionsCallback } from '~/components/PublicBoard/useGetPublicBoardMenuOptionsCallback';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { BoardTableRow, type BoardTableRowProps } from '~/components/TableView/BoardTableRow';
import { MobileTableRowContainer } from '~/components/TableView/MobileTableRow/MobileTableRowContainer';
import { PublicBoardNameCell } from '~/components/TableView/PrivateTableView/PublicBoardNameCell';
import { PublicBoardCustomFieldTableCell } from '~/components/TableView/TableRow/CustomFieldTableCell/PublicBoardCustomFieldTableCell';
import { DesktopTableRowContainer } from '~/components/TableView/TableRow/DesktopTableRowContainer';
import { type BasicTableRowProps } from '~/components/TableView/types';
import { TABLE_ROW } from '~/constants/testIDs';
import { useOpenPublicBoardInNewTab } from '~/hooks/useOpenPublicBoardInNewTab';
import { usePublicSelectionMenuOptions } from '~/hooks/usePublicSelectionMenuOptions';
import { useWorkspaceHorizontalPadding } from '~/hooks/useWorkspaceHorizontalPadding';
import { usePublicBoard } from '~/providers/PublicBoardProvider';
import { canChangeBoardCustomFields } from '~/utils/permissions/boardPermissions';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';

export type PublicBoardTableRowProps = {
  /**
   * `react-virtualized` warns that `style` property should be included in the rendered cell for positioning
   * so we've separated it from the `tx` prop
   * */
  style: CSSProperties;
} & BasicTableRowProps<PublicBoard>;

export const PublicBoardTableRow = memo(({ data: board, style }: PublicBoardTableRowProps) => {
  const { getSelectionMenuOptions } = usePublicSelectionMenuOptions();
  const { getBoardMenuOptionsCallback } = useGetPublicBoardMenuOptionsCallback();
  const { isAboveMediumScreen } = useBreakpointsContext();
  const { responsiveHorizontalPadding } = useWorkspaceHorizontalPadding();
  const { permissions } = usePublicPermissions();
  const getItemMenuOptions = getBoardMenuOptionsCallback({ item: board });
  const { onNavigateToBoard } = usePublicBoard();
  const { openPublicBoardInNewTab } = useOpenPublicBoardInNewTab();
  // The row contract passes a tracking location, but board navigation tracks nothing — the old
  // shared goToPublicBoard ignored it too (its `_location` param was never read).
  const onItemClick = useCallback(
    (_trackLocation: AirActionTrackingLocation) => onNavigateToBoard({ boardId: board.id, boardTitle: board.title }),
    [board, onNavigateToBoard],
  );
  const onItemCmdClick = useCallback(() => openPublicBoardInNewTab({ board }), [board, openPublicBoardInNewTab]);

  const canDownload = canGenerateZip(permissions);
  const canEditCustomFields = canChangeBoardCustomFields(permissions);
  const isSelectable = canDownload || canEditCustomFields;
  const memoizedBoardProps: ItemProps = useMemo(
    () => ({
      isSelectable,
      onItemClick,
      onItemCmdClick,
      getSelectionMenuOptions,
      getItemMenuOptions,
    }),
    [getItemMenuOptions, getSelectionMenuOptions, isSelectable, onItemClick, onItemCmdClick],
  );

  const renderNameCell: BoardTableRowProps<PublicBoard>['renderNameCell'] = useCallback(
    ({ isSelected, isHovering }) => (
      <PublicBoardNameCell isHovering={isHovering} isSelected={isSelected} board={board} {...memoizedBoardProps} />
    ),
    [board, memoizedBoardProps],
  );

  return isAboveMediumScreen ? (
    <DesktopTableRowContainer
      style={style}
      // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
      data-testid={`${TABLE_ROW}-${GalleryItemType.board}`}
      data-title={board.title}
    >
      <BoardTableRow
        renderNameCell={renderNameCell}
        data={board}
        boardProps={memoizedBoardProps}
        CustomFieldTableCell={PublicBoardCustomFieldTableCell}
        canSelect={isSelectable}
      />
    </DesktopTableRowContainer>
  ) : (
    <MobileTableRowContainer
      style={{
        paddingLeft: responsiveHorizontalPadding,
        paddingRight: responsiveHorizontalPadding,
        ...style,
      }}
    >
      {renderNameCell({ isSelected: false, isHovering: false })}
      <IconButton
        adsIcon
        appearance="ghost"
        color="gray"
        icon={ChevronRightIcon}
        // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
        onClick={() => {
          memoizedBoardProps.onItemClick('table-cell');
        }}
        size="extra-large"
        className="h-full"
        label="View board"
      />
    </MobileTableRowContainer>
  );
}, isEqual);

PublicBoardTableRow.displayName = 'PublicBoardTableRow';
